import { BoxProps, createBox } from "@shopify/restyle";
import * as React from "react";
import { ActivityIndicator } from "react-native";
import type { AccessibilityRole, AccessibilityState } from "react-native";
import { buttonVariants, theme, Theme } from "@theme";
import LBox, { LBoxProps } from "./LBox";
import LText from "./LText";
import { useState } from "react";
import LIcon, { IconName } from "./LIcon";
import LPressable, { LPressableProps } from "./LPressable";

type RestyleProps = BoxProps<Theme>;

export type ButtonProps = RestyleProps & {
  accessibilityHint?: string;
  accessibilityLabel?: string;
  accessibilityRole?: AccessibilityRole;
  accessibilityState?: AccessibilityState;
  applyOpacityWhenDisabled?: boolean;
  applyStyles?: boolean;
  enabled?: boolean;
  gradientBorder?: boolean;
  hideLabel?: boolean;
  iconMargin?: keyof typeof theme.spacing;
  iconName?: IconName;
  iconSize?: number;
  label?: string;
  labelNode?: React.ReactNode;
  onPress: () => Promise<void> | void;
  reverseIconPlacement?: boolean;
  sentryEventName: string | null;
  testID?: string;
  textGradient?: string[];
  textVariant?: keyof typeof theme.textVariants;
  variant?: keyof typeof buttonVariants;
  gradientDirection?: LBoxProps["gradientDirection"];
  lPressableStyle?: LPressableProps["style"];
};

const GradientBorder = ({ children }: { children: React.ReactNode }) => {
  return (
    <LBox
      gradientDirection="horizontal"
      backgroundColor={[
        "buttonBackgroundPrimaryLeft",
        "buttonBackgroundPrimaryRight",
      ]}
      borderRadius={"m-l"}
      style={{
        padding: 1,
      }}
    >
      {children}
    </LBox>
  );
};

const ButtonComponent = ({
  applyOpacityWhenDisabled = true,
  accessibilityHint,
  accessibilityLabel,
  accessibilityRole,
  accessibilityState,
  applyStyles = true,
  enabled = true,
  gradientBorder = false,
  gradientDirection = "horizontal",
  hideLabel = false,
  iconMargin = "s2",
  iconName,
  iconSize,
  label,
  labelNode,
  onPress,
  reverseIconPlacement,
  sentryEventName,
  testID,
  textGradient,
  textVariant = "button",
  variant,
  lPressableStyle,
  ...rest
}: ButtonProps) => {
  const boxProps = {
    ...buttonVariants.defaults,
    ...(variant ? buttonVariants[variant] : buttonVariants.primary),
  };
  const [loading, setLoading] = useState(false);
  const handlePress = async () => {
    setLoading(true);
    await onPress();
    setLoading(false);
  };

  const BorderComponent = gradientBorder ? GradientBorder : React.Fragment;

  return (
    <LPressable
      sentryEventName={sentryEventName}
      fadeEnabled
      onPress={handlePress}
      disabled={!enabled || loading}
      accessibilityHint={accessibilityHint}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole={accessibilityRole}
      accessibilityState={accessibilityState}
      testID={testID}
      style={{
        opacity: applyOpacityWhenDisabled
          ? !enabled || loading
            ? 0.5
            : 1
          : undefined,
        ...lPressableStyle,
      }}
    >
      <BorderComponent>
        {/* @ts-expect-error ts type error */}
        <LBox
          testID={testID}
          {...rest}
          {...boxProps}
          {...(applyStyles ? {} : { style: {} })}
          gradientDirection={gradientDirection}
          flexDirection={reverseIconPlacement ? "row-reverse" : "row"}
          justifyContent="center"
          alignItems="center"
          gap={iconName ? iconMargin : "zero"}
        >
          {loading ? (
            <ActivityIndicator size={19} />
          ) : (
            <>
              {iconName ? (
                <LIcon name={iconName} fill="transparent" size={iconSize} />
              ) : null}
              {labelNode === undefined ? (
                <LText
                  variant={textVariant as any}
                  textAlign="center"
                  gradientColours={textGradient}
                  color={boxProps.color}
                >
                  {hideLabel ? " " : label}
                </LText>
              ) : (
                labelNode
              )}
            </>
          )}
        </LBox>
      </BorderComponent>
    </LPressable>
  );
};

const LButton = createBox<Theme, ButtonProps>(ButtonComponent);

export default LButton;
